import { useState } from 'react';
import { CustomerTypeChooser } from './CustomerTypeChooser';
import { CustomerFormParticular } from './CustomerFormParticular';
import { CustomerFormBusiness } from './CustomerFormBusiness';
import type { Customer, CreateCustomerInput, ParticularCustomer, BusinessCustomer } from '../types/customer.types';

type CustomerKind = 'PARTICULAR' | 'BUSINESS';

/**
 * Al editar, el tipo de cliente (Particular = persona natural / Comercial =
 * persona jurídica) se puede cambiar, igual que se elige al crearlo. Se
 * trasladan los datos comunes al otro formulario: nombre ↔ razón social,
 * cédula ↔ RUC, DV, correo, teléfono y direcciones. El Tipo de Receptor DGI
 * vuelve al valor por defecto del formulario destino para que se revise.
 * El BFF (`buildCustomerPayloadMdl17`) ya recalcula `recep_type` y el tipo de
 * documento a partir de `customerKind`, y la edición sigue usando el mismo id.
 */
function convertCustomerKind(customer: Customer, target: CustomerKind): Customer {
  if (customer.customerKind === target) return customer;

  // `recepType` no se copia a propósito (ver comentario de arriba).
  const common = customer;

  if (target === 'BUSINESS') {
    const particular = customer as ParticularCustomer;
    const fullName = `${particular.firstName || ''} ${particular.lastName || ''}`.trim();
    const converted: BusinessCustomer = {
      id: common.id,
      companyId: common.companyId,
      email: common.email,
      phone: common.phone,
      address: common.address,
      isTaxExempt: common.isTaxExempt,
      taxExemptionCode: common.taxExemptionCode,
      isDeleted: common.isDeleted,
      createdAt: common.createdAt,
      updatedAt: common.updatedAt,
      customerKind: 'BUSINESS',
      legalName: fullName,
      comercialName: fullName,
      ruc: particular.idNumber || '',
      dv: particular.dv,
      countryId: particular.countryId,
    };
    return converted;
  }

  const business = customer as BusinessCustomer;
  const [firstWord, ...restWords] = (business.legalName || '').trim().split(/\s+/);
  const converted: ParticularCustomer = {
    id: common.id,
    companyId: common.companyId,
    email: common.email,
    phone: common.phone,
    address: common.address,
    isTaxExempt: common.isTaxExempt,
    taxExemptionCode: common.taxExemptionCode,
    isDeleted: common.isDeleted,
    createdAt: common.createdAt,
    updatedAt: common.updatedAt,
    customerKind: 'PARTICULAR',
    firstName: firstWord || '',
    lastName: restWords.join(' '),
    idNumber: business.ruc || '',
    dv: business.dv,
    countryId: business.countryId,
  };
  return converted;
}

const KIND_OPTIONS: Array<{ value: CustomerKind; label: string; hint: string }> = [
  { value: 'PARTICULAR', label: 'Particular', hint: 'Persona natural' },
  { value: 'BUSINESS', label: 'Comercial', hint: 'Persona jurídica' },
];

interface CustomerFormProps {
  customer?: Customer;
  onSubmit: (data: CreateCustomerInput) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

/**
 * Router del formulario de cliente — reemplaza al formulario único que
 * mezclaba Particular/Comercial en un solo esquema (mdl17). El portal legado
 * (`EditAndCreateCustomerForm.cshtml`) tiene DOS formularios distintos según
 * el tipo de cliente:
 *   - Alta nueva: primero pregunta el tipo (`CustomerTypeChooser`), luego
 *     muestra el formulario correspondiente.
 *   - Edición: se entra directo al formulario de `customer.customerKind`, con
 *     un selector arriba para cambiar el tipo (Particular ↔ Comercial).
 */
export function CustomerForm({ customer, onSubmit, onCancel, isLoading }: CustomerFormProps) {
  const [chosenKind, setChosenKind] = useState<CustomerKind | null>(
    customer?.customerKind ?? null
  );

  if (!chosenKind) {
    return <CustomerTypeChooser onSelect={setChosenKind} onCancel={onCancel} />;
  }

  const formCustomer = customer ? convertCustomerKind(customer, chosenKind) : undefined;
  const kindChanged = !!customer && customer.customerKind !== chosenKind;

  const kindSelector = customer ? (
    <div className="shrink-0 px-7 pt-5" data-testid="customer-form-kind-selector">
      <p className="mb-2 text-[12px] font-semibold text-secondary-600 dark:text-secondary-300">Tipo de cliente</p>
      <div className="inline-flex rounded-lg border border-secondary-200 p-1 dark:border-secondary-700" role="radiogroup" aria-label="Tipo de cliente">
        {KIND_OPTIONS.map((option) => {
          const selected = chosenKind === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setChosenKind(option.value)}
              disabled={isLoading}
              data-testid={`customer-form-kind-${option.value.toLowerCase()}-button`}
              className={`rounded-md px-4 py-1.5 text-[13px] font-semibold transition-colors ${
                selected
                  ? 'bg-teal-500/10 text-[#0d9488]'
                  : 'text-secondary-500 hover:text-secondary-800 dark:text-secondary-400 dark:hover:text-white'
              }`}
            >
              {option.label} <span className="font-normal opacity-75">· {option.hint}</span>
            </button>
          );
        })}
      </div>
      {kindChanged && (
        <p className="mt-2 text-[12px] text-warning-600 dark:text-warning-400" data-testid="customer-form-kind-changed-hint">
          Al guardar, el cliente pasará a ser {chosenKind === 'BUSINESS' ? 'Comercial (persona jurídica)' : 'Particular (persona natural)'}.
          Revise la identificación, el DV y el Tipo de Receptor DGI.
        </p>
      )}
    </div>
  ) : null;

  if (chosenKind === 'BUSINESS') {
    return (
      <>
        {kindSelector}
        <CustomerFormBusiness
          key="BUSINESS"
          customer={formCustomer?.customerKind === 'BUSINESS' ? formCustomer : undefined}
          onSubmit={onSubmit}
          onCancel={onCancel}
          isLoading={isLoading}
        />
      </>
    );
  }

  return (
    <>
      {kindSelector}
      <CustomerFormParticular
        key="PARTICULAR"
        customer={formCustomer?.customerKind === 'PARTICULAR' ? formCustomer : undefined}
        onSubmit={onSubmit}
        onCancel={onCancel}
        isLoading={isLoading}
      />
    </>
  );
}
